import { type Database } from "@fitness-app/supabase";

import { type UserRoleEnum } from "..";
import { type VideoProcessingStatus } from "./VideoProcessing";

export type ChatChannelEntity = Database["public"]["Tables"]["chat_channel"]["Row"];
export type ChatMemberEntity = Database["public"]["Tables"]["chat_member"]["Row"];
export type ChatMessageEntity = Database["public"]["Tables"]["chat_message"]["Row"];
export type ChatMessageMetadataEntity = Database["public"]["Tables"]["chat_message_metadata"]["Row"];
export type UnreadMessagesEntity = Database["public"]["Tables"]["unread_messages"]["Row"];

export enum MessageType {
  Standard = "standard",
  WithContext = "withContext",
  System = "system",
}

export enum ChatType {
  TwoWay = "two_way",
  OneWay = "one_way",
  TwoWayMarketing = "two_way_marketing",
  OneWayMarketing = "one_way_marketing",
}

/** The additional announcement ("marketing") channel of a client — TRE-801. */
export const isAnnouncementChannel = (type: ChatType | string): boolean =>
  type === ChatType.OneWayMarketing || type === ChatType.TwoWayMarketing;

export enum ChatStatus {
  Inactive = "inactive",
  Active = "active",
  Archived = "archived",
}

export interface ChatMember {
  uid: string;
  clientId?: string | null;
  email: string;
  avatarUrl: string | null;
  role: UserRoleEnum;
  name: string;
  disabled?: true;
  firstName?: string | null;
  lastName?: string | null;
}

export type MessageReaction = {
  icon: string;
  authorId: string;
  iconCode: string | null;
  addedAt: number;
  iconName: string | null;
};

export type Reactions = Array<MessageReaction>;
export enum MessageInContext {
  NewExerciseComment = "NEW_EXERCISE_COMMENT",
  UpdateExerciseComment = "UPDATE_EXERCISE_COMMENT",
  AddDishComment = "ADD_DISH_COMMENT",
  UpdateDishComment = "UPDATE_DISH_COMMENT",
  AddRecordingFeedback = "ADD_EXERCISE_VIDEO_RECORDING_FEEDBACK",
}

export interface MessageContext {
  type: MessageInContext;
  metadata: Record<string, string | number | null | undefined>;
}

export interface FileErrorMessage {
  label: string;
  interpolation?: Record<string, string | number | undefined>;
}

export type GifProvider = "klipy";

/**
 * A GIF picked from an external provider (TRE-2104). Hot-linked from the provider's CDN — its
 * terms forbid re-hosting — so the backend attachment post-processing has to leave it alone.
 */
export interface GifAttachment {
  provider: GifProvider;
  slug: string;
  width: number;
  height: number;
  /** Still frame, used as the placeholder while the animation loads. */
  previewUrl?: string | null;
  mp4Url?: string | null;
}

export interface FileMessageContent {
  uid: string;
  originalName: string;
  shortName: string;
  size: number;
  contentType: string;
  duration?: number | null;
  extension: string;
  url: string;
  thumbUrl?: string | null;
  status?: never;
  errorMessage?: never;
  progress?: never;
  metering?: number[] | null;
  gif?: GifAttachment | null;
  /**
   * Set once a video attachment has been re-encoded into a web-playable MP4 (TRE-1784).
   * `url` then points at the derived file and `sourceUrl` keeps the original upload, so
   * the clients can still offer the untouched file for download.
   */
  videoProcessingStatus?: VideoProcessingStatus | null;
  sourceUrl?: string | null;
}

// `unknown` on purpose: the file lists mix uploads-in-progress (no `gif` key at all) with sent
// attachments, and a `Pick<..., "gif">` parameter trips TS's weak-type check on the former.
export const isGifAttachment = (file: unknown): file is FileMessageContent & { gif: GifAttachment } =>
  typeof file === "object" && file !== null && Boolean((file as Partial<FileMessageContent>).gif?.slug);

export type LinkPreviewKind = "website" | "article" | "video" | "tweet";

/**
 * Unfurled metadata of a URL found in a message's text (Open Graph / Twitter Card / oEmbed).
 * Resolved once by chat-service after the insert and stored on the message, so every viewer
 * renders the same card without hitting the page themselves. Image and favicon are hot-linked.
 */
export interface LinkPreview {
  /** The URL exactly as it appeared in the message — the client matches it against the text. */
  url: string;
  /** Where the fetch ended after redirects; what the card opens. */
  resolvedUrl: string;
  kind: LinkPreviewKind;
  title: string | null;
  description: string | null;
  siteName: string | null;
  imageUrl: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  faviconUrl?: string | null;
  /** Tweet cards: the post's author, the handle without "@". */
  author?: { name: string; handle: string | null; url: string | null } | null;
  fetchedAt: string;
}

/** Message text is not editable, so previews are resolved exactly once: `[]` means "checked, nothing to show". */
export const getLinkPreviews = (message: Pick<BaseMessage, "extra"> | null | undefined): LinkPreview[] =>
  message?.extra?.linkPreviews ?? [];

type UUID = string;

export type MessageVisibleBy = "all" | UUID | UserRoleEnum;

export type MessageIssueType = "churn" | "app_bugs" | "unhappy_client" | "coach_mistake" | "other";

export type DissatisfactionDetails = {
  emotionalBarriers: boolean;
  lackOfResults: boolean;
  physicalDifficulties: boolean;
  lackOfSupport: boolean;
  disappointment: boolean;
  riskLevel: "low" | "medium" | "high" | "critical";
  recommendedAction: string;
};

export type MessageAgentReplyStatus = "suggested" | "sent_automatically" | "accepted" | "dismissed";
export type MessageAgentReplyFeedbackVote = "up" | "down";

export interface BaseMessage extends Omit<
  ChatMessageEntity,
  "context" | "reactions" | "files" | "visibleBy" | "authorRole" | "metadata" | "extra"
> {
  authorRole: UserRoleEnum;
  reactions?: Reactions | null;
  context?: null | MessageContext;
  sendBy: string;
  files?: FileMessageContent[];
  visibleBy: MessageVisibleBy[];
  metadata?: {
    transcription?: string;
    context?: MessageContentContext | null;
    summary?: string | null;
    sentiment?: MessageSentiment | null;
    issueType?: MessageIssueType | null;
    dissatisfactionDetails?: null | DissatisfactionDetails;
    actionable?: boolean | null;
    agentReplyStatus?: MessageAgentReplyStatus | null;
    agentReplyText?: string | null;
    agentReplyOwnerTeamMemberId?: string | null;
    agentReplyGeneratedAt?: string | null;
    agentReplyResolvedAt?: string | null;
    agentReplyResolvedByUserId?: string | null;
    agentReplySentMessageId?: string | null;
    agentReplyFeedbackVote?: MessageAgentReplyFeedbackVote | null;
    agentReplyFeedbackComment?: string | null;
    agentReplyFeedbackSubmittedAt?: string | null;
    agentReplyFeedbackUserId?: string | null;
  };
  extra?: null | {
    version: 1 | 2;
    /** Link cards for URLs in `content`, resolved by chat-service after the insert. */
    linkPreviews?: LinkPreview[] | null;
  };
}

export interface StandardMessage extends BaseMessage {
  type: MessageType.Standard;
}

export interface ContextMessage extends BaseMessage {
  type: MessageType.WithContext;
  context: MessageContext;
}

export interface SystemMessage extends BaseMessage {
  type: MessageType.System;
}

export type ChatMessage = StandardMessage | ContextMessage | SystemMessage;

export interface ChatChannel extends Omit<ChatChannelEntity, "members" | "metadata"> {
  assignedUsers: string[];
  members: Record<string, ChatMember>;
  status: ChatStatus;
  type: ChatType;
  metadata?: null | {
    disabledAudioRecording?: boolean;
  };
}

export type ChatChannelWithLastMessage = ChatChannel & {
  lastMessage: ChatMessage | null;
  profiles: {
    email: string;
    id: string;
    avatarUrl: string;
    firstName: string;
    lastName: string;
  }[];
};

export type ChatMessageWithMetadata = ChatMessage & {
  status: "sending" | "sent" | "failed";
};

export interface ChatChannelWithMeta extends ChatChannelWithLastMessage {
  minimized: boolean;
}

export interface UnreadMessage {
  id: string;
  createdAt: string | undefined;
}

export interface EmailNotificationAboutUnread {
  timestamp: string;
  includedMessageIds: string[];
}

export interface UnreadMessages extends Omit<
  UnreadMessagesEntity,
  "perChannel" | "lastEmailNotificationAboutUnread" | "unreadMessages"
> {
  total: number;
  perChannel: Record<string, string[]>;
  unreadMessages: UnreadMessage[];
  lastEmailNotificationAboutUnread?: EmailNotificationAboutUnread | null;
}

export interface UnreadMessagesWithPendingEmailNotification extends UnreadMessages {
  messagesWithoutNotification: UnreadMessage[];
}

export interface ChannelWithMessages {
  groupedByDay: GroupedMessage[];
  channelId: string;
}

export interface GroupedMessage {
  date: string;
  messages: ChatMessage[];
}

export interface AddMessageEvent {
  type: "message";
  operation: "add";
  data: ChatMessage;
  senderId: string;
}

export interface UpdateMessageEvent {
  type: "message";
  operation: "update";
  data: Partial<ChatMessage> & { id: string };
  senderId: string;
}

export interface DeleteMessageEvent {
  type: "message";
  operation: "delete";
  data: Partial<ChatMessage> & { id: string };
  senderId: string;
}

export type ChatMessageEvent = UpdateMessageEvent | DeleteMessageEvent | AddMessageEvent;
export type IncomingHttpMessageUpdate = UpdateMessageEvent;
export type IncomingHttpMessageAdd = AddMessageEvent;
export type IncomingHttpMessageEvent = IncomingHttpMessageUpdate | IncomingHttpMessageAdd;

export interface InitialChatStateEvent {
  type: "initial";
  messages: ChatMessage[];
  room: RoomInfo;
}

export type RoomInfo = {
  id: string;
  users: {
    username: string;
    joinedAt: string;
    leftAt?: string;
    present: boolean;
    id: string;
  }[];
};

export type ChatIncomingRealtimeEvents = ChatMessageEvent;
export type ChatOutgoingRealtimeEvents = ChatMessageEvent | InitialChatStateEvent;
export type MessageSentiment = "neutral" | "positive" | "negative" | "highly_negative";
export type MessageContentContext =
  | "general"
  | "private_life"
  | "workouts"
  | "nutrition"
  | "consultation"
  | "churn"
  | "subscription_renewal"
  | "workouts_and_nutrition"
  | string;

export type ChatMessageMetadata = Omit<ChatMessageMetadataEntity, "metadata" | "context"> & {
  metadata: Record<string, string | null | number | boolean>;
  context: null | MessageContentContext;
};

export type ChatMessageMetadataWithTrainee = ChatMessageMetadata & {
  trainee: null | {
    email: string;
    firstName: string | null;
    lastName: string | null;
    assignedTrainerId: string | null;
  };
  message: {
    authorId: string;
    content: string;
  };
};
